import {useEffect, useState} from 'react';
import supabase from '../supabase/supabase';

export default function useSupabaseAuth() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [recoverySession, setRecoverySession] = useState(false);

  useEffect(() => {
    let mounted = true;
    const {data: {subscription}} = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;

      setSession(nextSession);

      if (event === 'PASSWORD_RECOVERY') {
        setRecoverySession(true);
        setAuthError('');
      } else if (event === 'USER_UPDATED') {
        setRecoverySession(false);
      } else if (event === 'SIGNED_OUT') {
        setRecoverySession(false);
        setAuthError('Your session ended. Sign in again to transcribe.');
      } else if (nextSession) {
        setAuthError('');
      }

      setLoading(false);
    });

    supabase.auth.getSession()
      .then(({data, error}) => {
        if (!mounted) return;
        if (error) setAuthError('Could not restore your session. Please sign in again.');
        setSession(data?.session || null);
        setLoading(false);
      })
      .catch(() => {
        if (!mounted) return;
        setAuthError('Could not restore your session. Check your connection and sign in again.');
        setLoading(false);
      });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email, password) => {
    setAuthError('');
    const {data, error} = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password
    });
    if (error) throw error;
    setSession(data.session);
    return data.session;
  };

  const signUp = async (email, password) => {
    const {data,error}=await supabase.auth.signUp({email:email.trim(),password,
      options:{emailRedirectTo:window.location.origin}});
    if(error)throw error;
    if(data.session)setSession(data.session);
    return data;
  };

  const requestPasswordReset = async (email, redirectTo) => {
    const {error} = await supabase.auth.resetPasswordForEmail(email.trim(), {redirectTo});
    if (error) throw error;
  };

  const updatePassword = async password => {
    const {data, error} = await supabase.auth.updateUser({password});
    if (error) throw error;
    setSession(data.session || session);
    setRecoverySession(false);
  };

  const signOut = async () => {
    const {error} = await supabase.auth.signOut();
    if (error) throw error;
    setSession(null);
    setRecoverySession(false);
  };

  return {
    session,
    loading,
    authError,
    recoverySession,
    clearAuthError: () => setAuthError(''),
    signIn,
    signUp,
    signOut,
    requestPasswordReset,
    updatePassword
  };
}
